/**
 * Horizontal carousel between the sibling entries of a day, for the entry
 * detail screen.
 *
 * The detail page is keyed by entry id, but entries are naturally ordered
 * within their day (the same `created_at asc` order the day view shows). This
 * hook loads that day's entries, locates the current one, and lets the user
 * page to the previous / next sibling with a horizontal swipe — the content
 * tracks the finger and slides out on commit (or fling), then navigates.
 *
 * Physics come from the shared recognizer (deadzone, axis-lock, fling,
 * rising-edge haptic), so this matches the calendar/day carousels. Swipes that
 * begin on controls, links or editable fields are ignored so the composer and
 * header buttons keep working, and `prefers-reduced-motion` skips the slide.
 */
import { type Accessor, createMemo, createSignal, onCleanup } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@journal/powersync";
import { useEntriesByDateQuery } from "@journal/shared/solid";

import { closestSelector } from "@/lib/gestures/guards";
import { vibrate } from "@/lib/gestures/haptics";
import { ENTRY_DETAIL_SWIPE } from "@/lib/gestures/presets";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

interface EntryCarouselOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  /** The entry currently shown. */
  entryId: Accessor<string | undefined>;
  /** Its day (drives which siblings are loaded). */
  entryDate: Accessor<string>;
  /** Navigate to a sibling entry by id. */
  navigate: (entryId: string) => void;
  /** Width source for the slide animation. */
  getSurfaceRef: () => HTMLElement | undefined;
}

export function useEntryCarousel(options: EntryCarouselOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);

  const siblings = useEntriesByDateQuery({
    db: options.db,
    drizzle: options.drizzle,
    userId: options.userId,
    staleTime: options.staleTime,
    dateKey: () => options.entryDate(),
  });

  const ids = createMemo(() => (siblings.data ?? []).map((entry) => entry.id));
  const currentIndex = () => {
    const id = options.entryId();
    return id ? ids().indexOf(id) : -1;
  };

  const surfaceWidth = () =>
    options.getSurfaceRef()?.clientWidth ?? ENTRY_DETAIL_SWIPE.fallbackWidthPx;

  /** Target sibling id for a directional step. delta>0 = next entry. */
  const targetId = (delta: number): string | undefined => {
    const index = currentIndex();
    if (index === -1) {
      return undefined;
    }
    return ids()[index + delta];
  };

  const snapBack = () => {
    setTransitioning(true);
    setDragX(0);
  };

  /** Animate a directional entry change. delta>0 = next (content slides left). */
  const animate = (delta: number) => {
    const id = targetId(delta);
    if (!id) {
      snapBack();
      return;
    }
    if (prefersReducedMotion()) {
      setDragX(0);
      options.navigate(id);
      return;
    }
    const width = surfaceWidth();
    setTransitioning(true);
    setDragX(delta > 0 ? -width : width); // current entry leaves toward travel
    vibrate(ENTRY_DETAIL_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      options.navigate(id);
      setTransitioning(false);
      setDragX(0);
    }, ENTRY_DETAIL_SWIPE.transitionMs);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: ENTRY_DETAIL_SWIPE.thresholdPx,
    fling: { ...ENTRY_DETAIL_SWIPE.fling },
    canStart: ({ target }) =>
      // Don't hijack drags that begin on the composer, controls, links or the
      // photo grid's own interactions.
      !closestSelector(target, "textarea, input, select, button, a, [contenteditable]"),
    onStart: () => {
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      // Resist dragging past the first / last sibling.
      const atEdge =
        (sample.dx > 0 && targetId(-1) === undefined) ||
        (sample.dx < 0 && targetId(1) === undefined);
      setDragX(atEdge ? sample.dx * 0.2 : sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        ENTRY_DETAIL_SWIPE.commitThresholdPx,
        surfaceWidth() * ENTRY_DETAIL_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next entry; swipe right (dx>0) → previous.
        animate(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      snapBack();
    },
  });

  const handlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  const transform = () => `translate3d(${dragX()}px, 0, 0)`;
  const transition = () =>
    transitioning() ? `transform ${ENTRY_DETAIL_SWIPE.transitionMs}ms ease-out` : "none";

  return { handlers, transform, transition };
}
